import type { ReactNode } from 'react';
import type { RightsDeclaration } from '../api/admin';
import type { RightsBasis } from '../api/types';
import ui from './ui.module.css';

/**
 * "Por que podemos mostrar esta imagem?" — asked before every upload and URL
 * import (legal review 2026-09-28). A picture found online is not free to use,
 * and a person's face needs their authorization (CC art. 20); the API refuses
 * to store a file without an answer (400 `rights_required`).
 */
export const RIGHTS_LABELS: Record<RightsBasis, string> = {
  own: 'Foto própria',
  licensed: 'Licenciada (banco de imagens, Creative Commons…)',
  authorized: 'Autorizada pelo titular / pessoa retratada',
  public_domain: 'Domínio público',
};

export const EMPTY_RIGHTS: { basis: RightsBasis | ''; credit: string } = { basis: '', credit: '' };

/** The declaration, or null while the basis is still unanswered. */
export const declared = (value: typeof EMPTY_RIGHTS): RightsDeclaration | null =>
  value.basis ? { basis: value.basis, credit: value.credit.trim() || null } : null;

export function RightsFields({
  value,
  onChange,
  idPrefix,
}: {
  value: typeof EMPTY_RIGHTS;
  onChange: (next: typeof EMPTY_RIGHTS) => void;
  idPrefix: string;
}): ReactNode {
  return (
    <div className={ui.row}>
      <select
        id={`${idPrefix}-rights`}
        aria-label="Direito de uso da imagem"
        className={ui.select}
        value={value.basis}
        onChange={(event) => onChange({ ...value, basis: event.target.value as RightsBasis | '' })}
      >
        <option value="">Direito de uso…</option>
        {(Object.keys(RIGHTS_LABELS) as RightsBasis[]).map((basis) => (
          <option key={basis} value={basis}>
            {RIGHTS_LABELS[basis]}
          </option>
        ))}
      </select>
      <input
        id={`${idPrefix}-credit`}
        aria-label="Crédito ou licença"
        className={ui.input}
        placeholder="Crédito / licença (opcional)"
        maxLength={200}
        value={value.credit}
        onChange={(event) => onChange({ ...value, credit: event.target.value })}
      />
    </div>
  );
}
